/* Shared photo cache + avatar for the LIQ portal.
   Optimizations:
   - Signed URL TTL is 1h (backend). Cached in memory + sessionStorage.
   - On first fetch, browser is asked to preload the image via new Image().
   - Batched signed URL fetch (30ms debounce) — one round trip for N agents. */
import React, { useEffect, useState } from 'react';
import { api } from '../api';

const SS_KEY = 'liq.photoCache.v2';
const TTL_MS = 55 * 60 * 1000; // 55 min (backend URL is 60 min)

function _loadSession() {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // prune expired
    const now = Date.now();
    for (const id of Object.keys(parsed)) if (!parsed[id] || parsed[id].expires < now) delete parsed[id];
    return parsed;
  } catch { return {}; }
}
function _saveSession(cache) {
  try {
    const obj = {};
    for (const [id, v] of cache.entries()) obj[id] = v;
    sessionStorage.setItem(SS_KEY, JSON.stringify(obj));
  } catch { /* quota exceeded etc */ }
}

// Hydrate memory map from sessionStorage
const PhotoCache = new Map(Object.entries(_loadSession()));
// Kick off image preloads for already-cached URLs
if (typeof window !== 'undefined') {
  for (const [, v] of PhotoCache) { if (v?.url) { const img = new Image(); img.src = v.url; } }
}

let _batchQueue = [];
let _batchTimer = null;
let _batchResolvers = new Map();

async function _flushBatch() {
  const ids = [..._batchQueue];
  _batchQueue = [];
  _batchTimer = null;
  const resolvers = _batchResolvers;
  _batchResolvers = new Map();
  if (ids.length === 0) return;
  try {
    const r = await api.post('/employees/photo-urls', { ids });
    const urls = r.data.urls || {};
    for (const id of ids) {
      const u = urls[id]?.signedUrl || null;
      if (u) {
        PhotoCache.set(id, { url: u, expires: Date.now() + TTL_MS });
        // preload image in background so it's already in browser cache when <img> renders
        try { const im = new Image(); im.src = u; } catch { /* ignore */ }
      }
      (resolvers.get(id) || []).forEach((fn) => fn(u));
    }
    _saveSession(PhotoCache);
  } catch (e) {
    for (const id of ids) (resolvers.get(id) || []).forEach((fn) => fn(null));
  }
}

function queuePhotoFetch(id) {
  return new Promise((resolve) => {
    const cur = _batchResolvers.get(id) || [];
    cur.push(resolve);
    _batchResolvers.set(id, cur);
    if (!_batchQueue.includes(id)) _batchQueue.push(id);
    if (!_batchTimer) _batchTimer = setTimeout(_flushBatch, 30);
  });
}

export function preloadPhotos(agentIds) {
  const need = agentIds.filter((id) => {
    const c = PhotoCache.get(id);
    return !c || c.expires < Date.now();
  });
  if (need.length === 0) return;
  Promise.all(need.map((id) => queuePhotoFetch(id)));
}

export function useAgentPhoto(agent) {
  const [url, setUrl] = useState(() => {
    const c = agent?.id ? PhotoCache.get(agent.id) : null;
    return c && c.expires > Date.now() ? c.url : null;
  });
  useEffect(() => {
    if (!agent?.id) return;
    const c = PhotoCache.get(agent.id);
    if (c && c.expires > Date.now()) { setUrl(c.url); return; }
    let mounted = true;
    queuePhotoFetch(agent.id).then((u) => { if (mounted) setUrl(u); });
    return () => { mounted = false; };
  }, [agent?.id, agent?.photo?.mediaId, agent?.photo?.verified]);
  return url;
}

export function clearPhotoCache(id) {
  if (id) {
    PhotoCache.delete(id);
    _saveSession(PhotoCache);
  }
}

export function initials(name) {
  return (name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
}

export function AgentAvatar({ agent, size = 48, ring = false, glow = false }) {
  const url = useAgentPhoto(agent);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {ring && (
        <div className="absolute inset-[-3px] rounded-full opacity-80"
             style={{ background: 'conic-gradient(from 180deg, rgba(230,199,109,0.9), rgba(16,185,129,0.7), rgba(230,199,109,0.9))' }} />
      )}
      <div className="relative rounded-full overflow-hidden flex items-center justify-center bg-slate-900 border border-white/[0.08]"
           style={{ width: size, height: size, fontSize: size * 0.36,
                    boxShadow: glow ? '0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' : 'inset 0 1px 0 rgba(255,255,255,0.04)' }}>
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" loading="eager" decoding="async" />
        ) : (
          <span className="text-amber-200/80 font-serif">{initials(agent?.fullName)}</span>
        )}
      </div>
      {agent?.approvalStatus === 'Approved' && (
        <div className="absolute bottom-0 right-0 rounded-full bg-emerald-400 border-[2px] border-[#06080f]" style={{ width: size * 0.22, height: size * 0.22 }} />
      )}
    </div>
  );
}
